/**
 * Typed, validated environment config. Imported once at startup; throws a clear
 * error and halts the app if a required variable is missing or malformed —
 * fail fast rather than discover a bad config three screens deep.
 */
import { z } from 'zod';

const EnvSchema = z.object({
  VITE_API_BASE_URL: z.string().min(1).default('/api'),
  VITE_DEFAULT_ENV: z.enum(['dev', 'staging', 'prod']).default('prod'),
  /** Public base URL clients call (e.g. https://api.iflow…). Shown in the docs;
   *  falls back to the current origin when unset. */
  VITE_PUBLIC_API_URL: z.string().optional(),
});

function loadEnv() {
  const parsed = EnvSchema.safeParse(import.meta.env);
  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((i) => `  - ${i.path.join('.')}: ${i.message}`)
      .join('\n');
    throw new Error(`[iFlow] Invalid environment configuration:\n${issues}`);
  }
  return parsed.data;
}

export const env = loadEnv();

export type AppEnvironment = z.infer<typeof EnvSchema>['VITE_DEFAULT_ENV'];
